import React, { useState, useEffect, useRef } from 'react';
import { deviceCashierService, deviceRegisterService } from './posApi';
import { BaseTerminalService } from './BaseTerminalService';

// Kaspi POS Integration Service
class KaspiPosService extends BaseTerminalService {
    constructor() {
        super();
        this.baseUrl = null;
        this.isInitialized = false;
        this.deviceInfo = null;
        this.needsRegistration = false;
        this.initializationPromise = null;
        this.isDeviceAuthorized = false;

        // НЕ инициализируем сразу - только по требованию
    }

    // Проверка авторизации устройства
    checkDeviceAuthorization() {
        try {
            // Проверяем авторизацию устройства из localStorage или API
            const deviceRegisterInfo = localStorage.getItem('device_register_info');
            if (!deviceRegisterInfo) {
                console.log('Kaspi POS: Device not registered yet');
                this.needsRegistration = true;
                return false;
            }

            const deviceInfo = JSON.parse(deviceRegisterInfo);
            if (!deviceInfo || !deviceInfo.cash_register) {
                console.log('Kaspi POS: Device info incomplete');
                return false;
            }

            this.isDeviceAuthorized = true;
            return true;
        } catch (error) {
            console.error('Kaspi POS: Error checking device authorization:', error);
            return false;
        }
    }

    // Инициализация сервиса (только если устройство авторизовано)
    async init() {
        // Если уже инициализируется, возвращаем существующий промис
        if (this.initializationPromise) {
            return this.initializationPromise;
        }

        // Проверяем авторизацию устройства
        if (!this.checkDeviceAuthorization()) {
            console.log('Kaspi POS: Device not authorized, skipping initialization');
            return false;
        }

        this.initializationPromise = this._performInit();
        return this.initializationPromise;
    }

    async _performInit() {
        try {
            console.log('Kaspi POS: Starting initialization...');

            const deviceInfo = await this.getLocalDeviceInfo();
            if (!deviceInfo || !deviceInfo.cash_register) {
                console.warn('Kaspi POS: Device info or cash_register not found');
                this.needsRegistration = true;
                return false;
            }

            this.deviceInfo = deviceInfo;

            // Проверяем конфигурацию Kaspi
            const kaspiConfig = this.getKaspiConfig(deviceInfo);
            if (!kaspiConfig) {
                console.warn('Kaspi POS: Kaspi configuration not found');
                this.needsRegistration = true;
                return false;
            }

            this.baseUrl = `http://${kaspiConfig.pos_ip_address}:8080`;
            this.isInitialized = true;
            console.log(`Kaspi POS: Service initialized successfully with URL: ${this.baseUrl}`);
            return true;
        } catch (error) {
            console.error('Kaspi POS: Initialization error:', error);
            this.initializationPromise = null;
            return false;
        }
    }

    // Получение информации об устройстве из localStorage/API
    async getLocalDeviceInfo() {
        try {
            // Сначала пытаемся получить из API
            const { deviceRegisterService } = await import('./posApi');
            const deviceInfo = await deviceRegisterService.getRegisterInfo();
            return deviceInfo;
        } catch (error) {
            console.error('Kaspi POS: Error getting device info from API:', error);

            // Fallback к localStorage
            try {
                const deviceRegisterInfo = localStorage.getItem('device_register_info');
                if (!deviceRegisterInfo) return null;

                const info = JSON.parse(deviceRegisterInfo);
                return info;
            } catch (storageError) {
                console.error('Kaspi POS: Error parsing device info from localStorage:', storageError);
                return null;
            }
        }
    }

    // Получение конфигурации Kaspi
    getKaspiConfig(deviceInfo) {
        return this.getTerminalConfig(deviceInfo, 'kaspi');
    }

    // Создание конфигурации Kaspi POS
    createKaspiConfig(posIpAddress) {
        try {
            if (!this.deviceInfo) {
                throw new Error('Device info not available');
            }

            // Создаем новую конфигурацию Kaspi
            const kaspiConfig = {
                pos_type: 'kaspi',
                pos_ip_address: posIpAddress,
                enabled: true,
                created_at: new Date().toISOString()
            };

            // Обновляем device_register_info
            const updatedDeviceInfo = {
                ...this.deviceInfo,
                payment_methods: [
                    ...(this.deviceInfo.payment_methods || []).filter(method => method.pos_type !== 'kaspi'),
                    kaspiConfig
                ]
            };

            localStorage.setItem('device_register_info', JSON.stringify(updatedDeviceInfo));
            this.deviceInfo = updatedDeviceInfo;

            console.log('Kaspi POS: Configuration created successfully');
            return kaspiConfig;
        } catch (error) {
            console.error('Kaspi POS: Error creating Kaspi config:', error);
            return null;
        }
    }

    // Регистрация POS с IP адресом
    async registerPOS(posIpAddress) {
        try {
            // Проверяем авторизацию устройства перед регистрацией
            if (!this.checkDeviceAuthorization()) {
                throw new Error('Device not authorized');
            }

            // Создаем конфигурацию
            const kaspiConfig = this.createKaspiConfig(posIpAddress);
            if (!kaspiConfig) {
                throw new Error('Failed to create Kaspi configuration');
            }

            this.baseUrl = `http://${posIpAddress}:8080`;

            // Регистрируемся
            const registered = await this.register();
            if (registered) {
                this.needsRegistration = false;
                this.isInitialized = true;
                return true;
            }

            return false;
        } catch (error) {
            console.error('Kaspi POS: Registration error:', error);
            return false;
        }
    }


// Fetch с таймаутом и проверкой статуса
    async fetchWithTimeout(url, options = {}, timeout = 30000) {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout);

        try {
            const response = await fetch(url, {
                ...options,
                signal: controller.signal
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            return response;
        } catch (error) {
            clearTimeout(timeoutId);

            if (error.name === 'AbortError') {
                throw new Error('Превышен таймаут запроса (30 секунд)');
            }

            throw error;
        }
    }

    // Выполнение запроса
    async makeAuthenticatedRequest(url, options = {}) {
        if (!this.isInitialized) {
            const initialized = await this.init();
            if (!initialized) {
                throw new Error('Kaspi POS service not initialized - device not authorized');
            }
        }

        const headers = {
            'Content-Type': 'application/json',
            ...options.headers
        };

        return this.fetchWithTimeout(url, {
            ...options,
            headers
        });
    }

    // Проверка доступности хоста
    async checkHostAvailability(host) {
        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 секунд для проверки

            const response = await fetch(`http://${host}:8080/v2/deviceinfo`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
                signal: controller.signal
            });

            clearTimeout(timeoutId);
            return response.ok;
        } catch (error) {
            return false;
        }
    }

    // Начало оплаты
    async startPayment(amount, ownCheque = false) {
        try {
            const url = `${this.baseUrl}/v2/payment?amount=${amount}&owncheque=${ownCheque}`;
            const response = await this.makeAuthenticatedRequest(url);
            const data = await response.json();

            if (data.statusCode === 0 && data.data) {
                return {
                    success: true,
                    processId: data.data.processId,
                    status: data.data.status,
                    data: data.data // Возвращаем полные данные
                };
            } else {
                return {
                    success: false,
                    error: data.errorText || 'Payment start failed'
                };
            }
        } catch (error) {
            return {
                success: false,
                error: error.message
            };
        }
    }

    // Начало возврата
    async startRefund(amount, transactionData, ownCheque = false) {
        try {
            const method = transactionData.chequeInfo.method;
            const transactionId = method === 'qr' ? transactionData.chequeInfo.orderNumber : transactionData.chequeInfo.rrn;

            const url = `${this.baseUrl}/v2/refund?amount=${amount}&method=${method}&transactionId=${transactionId}&owncheque=${ownCheque}`;
            const response = await this.makeAuthenticatedRequest(url);
            const data = await response.json();

            if (data.statusCode === 0 && data.data) {
                return {
                    success: true,
                    processId: data.data.processId,
                    status: data.data.status
                };
            } else {
                return {
                    success: false,
                    error: data.errorText || 'Refund start failed'
                };
            }
        } catch (error) {
            return {
                success: false,
                error: error.message
            };
        }
    }

    // Проверка статуса транзакции
    async getTransactionStatus(processId) {
        try {
            const url = `${this.baseUrl}/v2/status?processId=${processId}`;
            const response = await this.makeAuthenticatedRequest(url);
            const data = await response.json();

            if (data.statusCode === 0 && data.data) {
                return {
                    success: true,
                    ...data.data
                };
            } else {
                return {
                    success: false,
                    error: data.errorText || 'Status check failed'
                };
            }
        } catch (error) {
            // Проверяем тип ошибки
            if (error.message.includes('таймаут') || error.message.includes('timeout')) {
                throw new Error('Таймаут при проверке статуса транзакции');
            }

            if (error.message.includes('HTTP')) {
                throw new Error(`Ошибка сервера: ${error.message}`);
            }

            throw new Error(`Ошибка подключения: ${error.message}`);
        }
    }

    // Актуализация статуса транзакции
    async actualizeTransaction(processId) {
        try {
            const url = `${this.baseUrl}/v2/actualize?processId=${processId}`;
            const response = await this.makeAuthenticatedRequest(url);
            const data = await response.json();

            if (data.statusCode === 0 && data.data) {
                return {
                    success: true,
                    ...data.data
                };
            } else {
                return {
                    success: false,
                    error: data.errorText || 'Actualization failed'
                };
            }
        } catch (error) {
            return {
                success: false,
                error: error.message
            };
        }
    }

    // Получение информации об устройстве
    async getDeviceInfo() {
        try {
            const url = `${this.baseUrl}/v2/deviceinfo`;
            const response = await this.makeAuthenticatedRequest(url);
            const data = await response.json();

            if (data.statusCode === 0 && data.data) {
                return {
                    success: true,
                    ...data.data
                };
            } else {
                return {
                    success: false,
                    error: data.errorText || 'Device info failed'
                };
            }
        } catch (error) {
            return {
                success: false,
                error: error.message
            };
        }
    }

    // Словарь кастомных сообщений для статусов
    getCustomStatusMessages() {
        return {
            'Initialize': 'Отправляем сумму в терминал',
            'ProcessCancelled': 'Операция отменена вами',
            'QrTransactionFailure': 'Покупка отменена',
            'QrTransactionSuccess': 'Покупка оплачена',
            'WaitForPinCode': 'Ожидание ввода ПИН-кода',
            'ProcessRefund': 'Обработка возврата',
            'WaitUser': 'Ожидаем оплату',
            'WaitForQrConfirmation': 'Ожидаем подтверждение клиента',
            'ProcessingCard': 'Обработка платежа',
            'CardTransactionFailure': 'Покупка отклонена',
            'CardTransactionSuccess': 'Покупка оплачена'
        };
    }

// Обработка статуса с кастомными сообщениями
    processTransactionStatus(status) {
        const customMessages = this.getCustomStatusMessages();

        if (status.success) {
            // Обрабатываем ProcessCancelled как fail
            if (status.status === 'unknown' && status.subStatus === 'ProcessCancelled') {
                return {
                    ...status,
                    status: 'fail',
                    customMessage: customMessages['ProcessCancelled'],
                    message: customMessages['ProcessCancelled']
                };
            }

            // Обрабатываем QrTransactionFailure как fail
            if (status.status === 'unknown' && status.subStatus === 'QrTransactionFailure') {
                return {
                    ...status,
                    status: 'fail',
                    customMessage: customMessages['QrTransactionFailure'],
                    message: customMessages['QrTransactionFailure']
                };
            }

            // Добавляем кастомное сообщение для других статусов
            if (status.subStatus && customMessages[status.subStatus]) {
                return {
                    ...status,
                    customMessage: customMessages[status.subStatus],
                    message: customMessages[status.subStatus]
                };
            }
        }

        return status;
    }

    // Мониторинг транзакции до завершения
    async monitorTransaction(processId, onStatusUpdate = null) {
        let consecutiveErrors = 0;
        const maxConsecutiveErrors = 3;

        const checkStatus = async () => {
            try {
                const status = await this.getTransactionStatus(processId);
                consecutiveErrors = 0; // Сбрасываем счетчик при успешном запросе

                // Обрабатываем статус с кастомными сообщениями
                const processedStatus = this.processTransactionStatus(status);

                if (onStatusUpdate) {
                    onStatusUpdate(processedStatus);
                }

                if (processedStatus.success) {
                    if (processedStatus.status === 'success' || processedStatus.status === 'fail') {
                        return processedStatus;
                    } else if (processedStatus.status === 'unknown' && processedStatus.subStatus !== 'ProcessCancelled' && processedStatus.subStatus !== 'QrTransactionFailure') {
                        // Пробуем актуализировать только если не ProcessCancelled
                        const actualized = await this.actualizeTransaction(processId);
                        if (actualized.success && (actualized.status === 'success' || actualized.status === 'fail')) {
                            return this.processTransactionStatus(actualized);
                        }
                    }
                }
            } catch (error) {
                consecutiveErrors++;
                console.error(`Kaspi POS: Status check error #${consecutiveErrors}:`, error);

                // Проверяем подключение после нескольких ошибок
                if (consecutiveErrors >= maxConsecutiveErrors) {
                    const hostAvailable = await this.checkHostAvailability(this.baseUrl.replace('http://', '').replace(':8080', ''));

                    if (!hostAvailable) {
                        return {
                            success: false,
                            status: 'fail',
                            error: 'Потеряно подключение к терминалу',
                            customMessage: 'Потеряно подключение к терминалу'
                        };
                    }
                }

                // Если много ошибок подряд, возвращаем ошибку
                if (consecutiveErrors >= 5) {
                    return {
                        success: false,
                        status: 'fail',
                        error: 'Превышено максимальное количество ошибок подключения',
                        customMessage: 'Превышено максимальное количество ошибок подключения'
                    };
                }
            }

            await new Promise(resolve => setTimeout(resolve, 1000));
            return checkStatus();
        };

        return checkStatus();
    }

    // Очистка ресурсов
    destroy() {
        this.initializationPromise = null;
        this.isInitialized = false;
    }

    // Сброс сервиса (для случаев деавторизации устройства)
    reset() {
        this.destroy();
        this.baseUrl = null;
        this.deviceInfo = null;
        this.needsRegistration = false;
        this.isDeviceAuthorized = false;
    }
}

// Глобальный экземпляр сервиса (но не инициализируется автоматически)
const kaspiPOSService = new KaspiPosService();

export { kaspiPOSService };
